import {
  CompRecommendationOutput,
  TKV2_ORDER_ATTACHMENT_API,
  schRecommendedCompsFilters,
} from '@aloft/toolkit-shared';
import { useQueryClient } from '@tanstack/react-query';

import { useGetCompRecommendation } from '../../../hooks/api/schema-hooks/useGetCompRecommendation.ts';
import { usePostCompRecommendation } from '../../../hooks/api/toolkitComponents.ts';
import { MILES_TO_METERS } from '../../map/constants.ts';
import { CompSearch } from '../types.ts';

export function useRecommendedProperties({ orderID }: { orderID: string }): {
  isIdle: boolean;
  isLoading: boolean;
  isError: boolean;
  data: CompRecommendationOutput | undefined;
  getProperties: (props: { search: CompSearch }) => void;
} {
  const recommendationMutator = usePostCompRecommendation();
  const attachmentQueryVariables = { pathParams: { orderID } };
  const attachmentQuery = useGetCompRecommendation(attachmentQueryVariables, {
    // we expect to not have a comp attachment if the user hasn't run
    // this yet - don't take the time to retry if that's the error that
    // we're getting.
    retry: (_, error) => {
      if (error?.status === 404) {
        return false;
      }
      return true;
    },
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });
  const queryClient = useQueryClient();
  const source = 'z'; // fall back to zillow if we don't have any c1 results

  const getProperties = ({ search }: { search: CompSearch }) => {
    if (recommendationMutator.isLoading) {
      return;
    }
    const body = TKV2_ORDER_ATTACHMENT_API.COMP_RECOMMENDATION.POST.REQUEST.BODY.parse({
      coordinates:
        search.geoSearch.t === 'polygon'
          ? search.geoSearch.coords.map(([longitude, latitude]) => ({
              latitude,
              longitude,
            }))
          : [],
      radius:
        search.geoSearch.t === 'radius'
          ? search.geoSearch.radiusInMeters / MILES_TO_METERS
          : undefined,
      filters: schRecommendedCompsFilters.parse(search.filters),
      matchCriteria: search.matchCriteria,
    });
    recommendationMutator.mutate(
      { pathParams: { orderID }, queryParams: { source }, body },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['api', 'v1', 'account'] });
          // make sure we refetch the attachment where we need it
          attachmentQuery.refetch();
        },
      },
    );
  };

  return {
    isIdle: recommendationMutator.isIdle,
    isLoading: recommendationMutator.isLoading || attachmentQuery.isLoading,
    isError: recommendationMutator.isError,
    data:
      !attachmentQuery.isLoading && !attachmentQuery.isError && attachmentQuery.data
        ? attachmentQuery.data.body
        : undefined,
    getProperties,
  };
}
